import React, { useState, useEffect } from 'react';
import {
  Button,
  Spin,
  Alert,
  Tag,
  Card,
} from 'antd';
import { MailOutlined, CloseOutlined, ReloadOutlined, ClockCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';

interface EmailMessage {
  id: string;
  subject: string;
  from: string;
  to: string[];
  receivedDateTime: string;
  bodyPreview: string;
  hasAttachments: boolean;
  importance: 'low' | 'normal' | 'high';
  isRead: boolean;
}

interface EmailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isFullWidth?: boolean;
}

export const EmailDrawer: React.FC<EmailDrawerProps> = ({ isOpen, onClose, isFullWidth = false }) => {
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'high': return 'red';
      case 'low': return 'cool-gray';
      default: return 'blue';
    }
  };

  const loadEmails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await apiService.getAuthToken();
      if (!token) {
        setError('Please sign in to access your emails. Email access requires Microsoft 365 authentication with appropriate permissions.');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/mcp/365mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tool: 'get_received_emails',
          parameters: {
            days: 7,
            count: 10
          }
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please sign in with Microsoft 365 to access emails.');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.result) {
        setEmails(data.result);
      } else {
        throw new Error(data.error || 'Failed to load emails');
      }
      
    } catch (error) {
      console.error('Failed to load emails:', error);
      setError(error instanceof Error ? error.message : 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen || isFullWidth) {
      loadEmails();
    }
  }, [isOpen, isFullWidth]);

  // If this is a full-width email tab, render differently
  if (isFullWidth) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-bg-primary)',
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 2rem',
          borderBottom: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-primary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '400' }}>
              Email
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Recent emails from your Microsoft 365 mailbox
            </p>
          </div>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadEmails}
            loading={loading}
            title="Refresh emails"
          >
            Refresh
          </Button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          )}

          {error && (
            <Alert
              type="error"
              message="Failed to load emails"
              description={error}
              style={{ marginBottom: '2rem' }}
              closable
              onClose={() => setError(null)}
            />
          )}

          {!loading && !error && emails.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '4rem',
              color: 'var(--color-text-secondary)'
            }}>
              <MailOutlined style={{ fontSize: '64px', marginBottom: '1rem', opacity: 0.5 }} />
              <h3>No recent emails found</h3>
              <p>Try refreshing or check your Microsoft 365 connection.</p>
            </div>
          )}

          <div style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))'
          }}>
            {emails.map((email) => (
              <Card
                key={email.id}
                style={{
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: email.isRead ? 'none' : '2px solid var(--sfc-stone-grey)',
                  opacity: email.isRead ? 0.8 : 1,
                }}
                onClick={() => {
                  // Handle email click
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '1rem'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: email.isRead ? 'normal' : '600',
                      fontSize: '1rem',
                      marginBottom: '0.5rem',
                      color: 'var(--color-text-primary)',
                      lineHeight: '1.3'
                    }}>
                      {email.subject || '(No Subject)'}
                    </div>
                    
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                      marginBottom: '0.5rem'
                    }}>
                      From: <strong>{email.from}</strong>
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    marginLeft: '1rem'
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <ClockCircleOutlined style={{ fontSize: '16px' }} />
                      {formatDate(email.receivedDateTime)}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {email.importance !== 'normal' && (
                        <Tag
                          color={getImportanceColor(email.importance)}
                        >
                          {email.importance}
                        </Tag>
                      )}
                      {email.hasAttachments && (
                        <Tag>📎</Tag>
                      )}
                      {!email.isRead && (
                        <Tag color="blue">New</Tag>
                      )}
                    </div>
                  </div>
                </div>
                
                {email.bodyPreview && (
                  <div style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-text-tertiary)',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {email.bodyPreview}
                  </div>
                )}
              </Card>
            ))}
          </div>

          {emails.length > 0 && (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: 'var(--color-text-secondary)',
              fontSize: '0.875rem'
            }}>
              Showing {emails.length} emails from the past 7 days
            </div>
          )}
        </div>
      </div>
    );
  }


  // Slide-out drawer mode
  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--color-overlay-dark)',  // Use theme overlay color
            zIndex: 9998,
            transition: 'opacity 0.3s ease'
          }}
          onClick={onClose}
        />
      )}

      {/* Drawer Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: isOpen ? 0 : '-400px',
          width: '400px',
          height: '100vh',
          backgroundColor: 'var(--color-bg-primary)',
          boxShadow: 'var(--shadow-heavy)',  // Use theme shadow
          zIndex: 9999,
          transition: 'right 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid var(--color-border-primary)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-primary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
              Recent Emails
            </h3>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
              Last 10 emails received
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={loadEmails}
              loading={loading}
              title="Refresh emails"
            />
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={onClose}
              title="Close drawer"
            />
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Loading description="Loading emails..." />
          </div>
        )}

        {error && (
          <InlineNotification
            kind="error"
            title="Failed to load emails"
            subtitle={error}
            style={{ marginBottom: '1rem' }}
            onClose={() => setError(null)}
          />
        )}

        {!loading && !error && emails.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem',
            color: 'var(--color-text-secondary)'
          }}>
            <MailOutlined style={{ fontSize: '48px', marginBottom: '1rem', opacity: 0.5 }} />
            <p>No recent emails found</p>
          </div>
        )}

        {emails.map((email) => (
          <Card
            key={email.id}
            style={{
              marginBottom: '0.75rem',
              padding: '1rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: email.isRead ? 'none' : '2px solid #1890ff',
              opacity: email.isRead ? 0.8 : 1,
            }}
            onClick={() => {
              // TODO: Open email detail or mark as read
              // Handle email click
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '0.5rem'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: email.isRead ? 'normal' : '600',
                  fontSize: '0.875rem',
                  marginBottom: '0.25rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'var(--color-text-primary)'
                }}>
                  {email.subject || '(No Subject)'}
                </div>
                
                <div style={{
                  fontSize: '0.75rem',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.25rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  From: {email.from}
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                marginLeft: '0.5rem'
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <ClockCircleOutlined style={{ fontSize: '12px' }} />
                  {formatDate(email.receivedDateTime)}
                </div>
                
                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                  {email.importance !== 'normal' && (
                    <Tag
                      type={getImportanceColor(email.importance)}
                      size="sm"
                    >
                      {email.importance}
                    </Tag>
                  )}
                  {email.hasAttachments && (
                    <Tag type="outline" size="sm">📎</Tag>
                  )}
                  {!email.isRead && (
                    <Tag type="blue" size="sm">New</Tag>
                  )}
                </div>
              </div>
            </div>
            
            {email.bodyPreview && (
              <div style={{
                fontSize: '0.75rem',
                color: 'var(--color-text-tertiary)',
                lineHeight: '1.3',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginTop: '0.5rem'
              }}>
                {email.bodyPreview}
              </div>
            )}
          </Card>
        ))}

        {emails.length > 0 && (
          <div style={{
            textAlign: 'center',
            padding: '1rem',
            color: 'var(--color-text-secondary)',
            fontSize: '0.75rem'
          }}>
            Showing last {emails.length} emails from the past 7 days
          </div>
        )}
        </div>
      </div>
    </>
  );
};